import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { colors } from '../theme/colors.js';
import logoBgless from '../assets/LOGO_BGLESS.png';

import {
  HeroSection,
  AccessTiersSection,
  InventorySection,
  WasteSection,
  AllInOneSection,
  AllDevicesSection,
  ComparisonSection,
  ClosingSection,
  SectionTracker,
} from '../components/blog/index.js';

/* ─── palette ─── */
const P = colors.primary;
const A = colors.accent;

/* ─── Sticky CTA pill ─── */
const StickyCTA = ({ onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      position: 'fixed',
      bottom: { xs: 20, md: 32 },
      right: { xs: 20, md: 32 },
      zIndex: 100,
      px: 3,
      py: 1.5,
      borderRadius: '50px',
      background: `linear-gradient(135deg, ${P}, ${A})`,
      color: '#fff',
      fontWeight: 700,
      fontSize: '0.85rem',
      cursor: 'pointer',
      boxShadow: `0 6px 24px rgba(69,39,160,0.35), 0 2px 8px rgba(255,45,85,0.25)`,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      transition: 'transform 0.25s, box-shadow 0.25s',
      '&:hover': {
        transform: 'translateY(-3px) scale(1.05)',
        boxShadow: `0 12px 36px rgba(69,39,160,0.45), 0 4px 12px rgba(255,45,85,0.35)`,
      },
    }}
  >
    Create Your Account <ArrowForward sx={{ fontSize: 16 }} />
  </Box>
);

/* ─── Back to login pill ─── */
const BackButton = ({ onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      position: 'fixed',
      top: { xs: 16, md: 24 },
      left: { xs: 16, md: 32 },
      zIndex: 110,
      display: 'flex',
      alignItems: 'center',
      gap: 0.8,
      px: 2,
      py: 1,
      borderRadius: '50px',
      background: 'rgba(255,255,255,0.72)',
      backdropFilter: 'blur(14px)',
      border: '1px solid rgba(255,255,255,0.5)',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        background: 'rgba(255,255,255,0.92)',
        transform: 'translateX(-2px)',
      },
    }}
  >
    <ArrowBack sx={{ fontSize: 16, color: P }} />
    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: P }}>
      Sign in
    </Typography>
  </Box>
);

/* ════════════════════════════════════════════════════════════════
   PAGE SHELL — storytelling flow with section tracker
   ════════════════════════════════════════════════════════════════ */
export const BlogPage = () => {
  const navigate = useNavigate();
  const goLogin = () => navigate('/login');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#FFFFFF',
        position: 'relative',
        overflowX: 'hidden',
      }}
    >
      <BackButton onClick={goLogin} />
      {/* Sticky logo — top right */}
      <Box
        component="img"
        src={logoBgless}
        alt="Karobar"
        sx={{
          position: 'fixed',
          top: { xs: 16, md: 24 },
          right: { xs: 16, md: 32 },
          zIndex: 110,
          width: { xs: 100, md: 150 },
          height: 'auto',
        }}
      />
      {!isMobile && <StickyCTA onClick={goLogin} />}
      {!isMobile && <SectionTracker />}

      {/* ── Storytelling sections ── */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* 1. Hero — The Edge */}
        <Box id="hero">
          <HeroSection onCTA={goLogin} />
        </Box>

        {/* 2. Access Tiers — Your Team */}
        <Box id="access-tiers">
          <AccessTiersSection />
        </Box>

        {/* 3. Inventory Intelligence */}
        <Box id="inventory">
          <InventorySection />
        </Box>

        {/* 4. Wastage Reduction */}
        <Box id="waste">
          <WasteSection />
        </Box>

        {/* 5. All-in-One Platform */}
        <Box id="all-in-one">
          <AllInOneSection />
        </Box>

        {/* 6. One Platform, All Devices */}
        <Box id="all-devices">
          <AllDevicesSection />
        </Box>

        {/* 7. Comparison */}
        <Box id="comparison">
          <ComparisonSection />
        </Box>

        {/* 8. Closing CTA */}
        <Box id="closing">
          <ClosingSection onCTA={goLogin} />
        </Box>
      </Box>
    </Box>
  );
};
